import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Shield, Scale, FileCheck, Eye } from 'lucide-react'
import SiteHeader from '../components/SiteHeader'
import SiteFooter from '../components/SiteFooter'
import { MotionTokens } from '../styles/motion-tokens'

const brandFont = {
  fontFamily: '"Helvetica Neue", "HelveticaNeue-UltraLight", -apple-system, sans-serif',
  fontWeight: 100,
} as const

const bodyFont = {
  fontFamily: '"Helvetica Neue", sans-serif',
  fontWeight: 300,
} as const

const sectionReveal = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: {
    duration: MotionTokens.sectionReveal.duration,
    ease: [0.22, 1, 0.32, 1],
  },
}

function GlassCard({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-xl border bg-white/[0.055] border-white/[0.09] backdrop-blur-sm hover:bg-white/[0.08] hover:border-white/[0.14] transition-all duration-300 ${className}`}>
      {children}
    </div>
  )
}

export default function GovernancePage() {
  return (
    <div className="min-h-screen bg-consciousness-deep">
      <SiteHeader />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="mb-6 flex justify-center">
              <Shield className="w-16 h-16 text-trust-blue" strokeWidth={1} />
            </div>
            <h1
              className="text-5xl md:text-7xl tracking-[0.15em] mb-6 text-white"
              style={brandFont}
            >
              {'GU\u039ARDI\u039AN'}
            </h1>
            <p className="text-xl md:text-2xl text-white/70 max-w-3xl mx-auto" style={bodyFont}>
              Oversight, accountability, and ethical leadership
            </p>
          </motion.div>
        </div>
      </section>

      {/* AI Ethics Policy */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div className="text-center mb-16" {...sectionReveal}>
            <h2
              className="text-4xl md:text-5xl tracking-[0.1em] mb-6 text-white"
              style={brandFont}
            >
              AI <span className="text-trust-blue">Ethics</span> Policy
            </h2>
            <p className="text-xl text-white/70 max-w-3xl mx-auto" style={bodyFont}>
              Our ethical framework ensures consciousness technology advances human welfare
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Eye, title: 'Transparency', desc: 'All AI decision pathways are auditable. We maintain open reporting on model behavior, bias detection, and intervention records.' },
              { icon: Scale, title: 'Fairness', desc: 'Continuous bias monitoring across protected attributes. Guardian-enforced guardrails prevent discriminatory outcomes in every model output.' },
              { icon: Shield, title: 'Accountability', desc: 'Named ethics officers, an independent review board, and quarterly public disclosures ensure meaningful human oversight at every level.' },
            ].map(({ icon: Icon, title, desc }) => (
              <motion.div key={title} {...sectionReveal}>
                <GlassCard>
                  <div className="p-8 text-center">
                    <div className="mb-4 flex justify-center">
                      <Icon className="w-10 h-10 text-trust-blue" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-xl tracking-wide mb-3 text-white" style={{ ...bodyFont, fontWeight: 300 }}>
                      {title}
                    </h3>
                    <p className="text-white/50" style={bodyFont}>{desc}</p>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Governance Framework */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div className="text-center mb-16" {...sectionReveal}>
            <h2
              className="text-4xl md:text-5xl tracking-[0.1em] mb-6 text-white"
              style={brandFont}
            >
              Data <span className="text-trust-blue">Governance</span> Framework
            </h2>
            <p className="text-xl text-white/70 max-w-3xl mx-auto" style={bodyFont}>
              Enterprise-grade data stewardship from ingestion through deletion
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: 'Data Classification', desc: 'Automated tiered classification from public to highly restricted, with policy-driven access controls enforced at the infrastructure layer.', items: ['Four-tier sensitivity model', 'Automated PII detection', 'Role-based access enforcement'] },
              { title: 'Lifecycle Management', desc: 'Defined retention schedules, immutable audit logs, and cryptographic deletion verification ensure data never outlives its purpose.', items: ['Policy-driven retention', 'Immutable audit trails', 'Verifiable deletion'] },
              { title: 'Cross-Border Compliance', desc: 'Region-aware data residency with automated routing to ensure compliance with GDPR, CCPA, and emerging sovereignty regulations.', items: ['Data residency controls', 'Transfer impact assessments', 'Multi-jurisdiction mapping'] },
            ].map(({ title, desc, items }) => (
              <motion.div key={title} {...sectionReveal}>
                <GlassCard>
                  <div className="p-8">
                    <h3 className="text-xl tracking-wide mb-3 text-trust-blue" style={{ ...bodyFont, fontWeight: 300 }}>
                      {title}
                    </h3>
                    <p className="text-white/50 mb-4" style={bodyFont}>{desc}</p>
                    <ul className="space-y-2 text-sm text-white/40">
                      {items.map((item) => (
                        <li key={item} style={bodyFont}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance & Audit */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div className="text-center mb-16" {...sectionReveal}>
            <h2
              className="text-4xl md:text-5xl tracking-[0.1em] mb-6 text-white"
              style={brandFont}
            >
              Compliance & <span className="text-trust-blue">Audit</span>
            </h2>
            <p className="text-xl text-white/70 max-w-3xl mx-auto" style={bodyFont}>
              Continuous compliance monitoring with independent third-party verification
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: 'SOC 2 Type II', desc: 'Annual third-party audit of security, availability, processing integrity, confidentiality, and privacy controls.' },
              { title: 'ISO 27001', desc: 'Information security management system certification with continuous improvement cycle and regular surveillance audits.' },
              { title: 'AI Act Readiness', desc: 'Proactive alignment with the EU AI Act, including risk classification, conformity assessment, and post-market monitoring.' },
            ].map(({ title, desc }) => (
              <motion.div key={title} {...sectionReveal}>
                <GlassCard>
                  <div className="p-8 text-center">
                    <div className="mb-4 flex justify-center">
                      <FileCheck className="w-10 h-10 text-trust-blue" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-xl tracking-wide mb-3 text-white" style={{ ...bodyFont, fontWeight: 300 }}>
                      {title}
                    </h3>
                    <p className="text-white/50" style={bodyFont}>{desc}</p>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6">
        <GlassCard className="max-w-4xl mx-auto">
          <div className="p-12 text-center">
            <h2
              className="text-4xl md:text-5xl tracking-[0.1em] mb-6 text-white"
              style={brandFont}
            >
              Learn More About Our Governance
            </h2>
            <p className="text-xl text-white/70 mb-12" style={bodyFont}>
              Request our detailed governance documentation and compliance certifications
            </p>
            <Link
              to="/contact"
              className="px-12 py-3 rounded-full bg-trust-blue text-white text-sm tracking-[0.06em] hover:bg-trust-blue/90 transition-all inline-block"
              style={bodyFont}
            >
              Request Documentation
            </Link>
          </div>
        </GlassCard>
      </section>

      <SiteFooter
        columns={[
          {
            title: 'Governance',
            links: [
              { label: 'AI Ethics Policy', href: '/governance' },
              { label: 'Data Governance', href: '/governance' },
              { label: 'Compliance & Audit', href: '/governance' },
            ],
          },
          {
            title: 'Company',
            links: [
              { label: 'Home', href: '/' },
              { label: 'Investors', href: '/investors' },
              { label: 'Contact', href: '/contact' },
            ],
          },
          {
            title: 'Legal',
            links: [
              { label: 'Privacy Policy', href: '/privacy' },
              { label: 'Terms of Service', href: '/terms' },
              { label: 'Cookie Policy', href: '/cookies' },
            ],
          },
        ]}
      />
    </div>
  )
}
